import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CarFront, ArrowLeft, Calendar, Truck, PauseCircle, PlayCircle, Pencil, Save, X, Image as ImageIcon,
  Ban, Trash2, Upload, AlertTriangle, Eye as EyeIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useApp, can } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  avGet, avUpdate, avHold, avResume, avDeliver, avAttachmentUrl,
  avUploadAttachment, avDeleteAttachment, avDelete,
} from "@/lib/api";
import { fmtDate, fmtDateTime, ROLE_LABELS } from "@/lib/format";
import {
  AV_STATUSES, AV_STATUS_TONES, statusLabel, HOLD_REASONS, CLAIM_TYPES,
  canEditAv, canHoldOrDeliverAv,
} from "./avShared";
import AgeingBadge from "./AgeingBadge";
import {
  EstimateTab, DocumentsTab, PartsTab, RepairTab, TimelineTab,
  AdvanceTab, BillingTab, CrmNotesTab,
} from "./AvDetailTabs";
import { LocTriggerModal } from "@/components/LocTriggerModal";
import { LocActiveBadge } from "@/components/LocActiveBadge";

const AvDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useApp();

  const [av, setAv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [holdOpen, setHoldOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);
  const [deliverOpen, setDeliverOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cnrOpen, setCnrOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Phase 2: real tab switching. Billing / Advance / CRM still pending.
  const [tab, setTab] = useState("overview");

  const load = async () => {
    setLoading(true);
    try {
      const doc = await avGet(id);
      setAv(doc);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load AV");
      navigate("/av/workbench");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const startEdit = () => {
    setForm({
      customer_name: av.customer_name || "",
      mobile_number: av.mobile_number || "",
      alternate_number: av.alternate_number || "",
      address: av.address || "",
      town: av.town || "",
      registration_number: av.registration_number || "",
      vin_no: av.vin_no || "",
      model_name: av.model_name || "",
      job_card_no: av.job_card_no || "",
      km_reading: av.km_reading || "",
      claim_type: av.claim_type || "insured",
      insurance_company: av.insurance_company || "",
      policy_end_date: av.policy_end_date || "",
      complaint: av.complaint || "",
      likely_delivery_date: av.likely_delivery_date || "",
      receiving_date: av.receiving_date || "",
      internal_remarks: av.internal_remarks || "",
      status: av.status || "vehicle_received",
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        km_reading: form.km_reading ? Number(form.km_reading) : null,
        actor: currentUser.name,
        actor_role: currentUser.role,
      };
      const updated = await avUpdate(id, payload);
      setAv(updated);
      setEditing(false);
      toast.success("AV updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !av) {
    return <div className="text-[12px] text-[#747684]">Loading AV…</div>;
  }

  const canEdit = canEditAv(currentUser) && av.status !== "delivered";
  const canAct = canHoldOrDeliverAv(currentUser);
  const canLocWrite = can(currentUser, "loc_write");
  const canLocView = can(currentUser, "loc_view");
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const vehiclePhotos = (av.attachments || []).filter((a) => a.kind === "vehicle_photo");
  const damagePhotos = (av.attachments || []).filter((a) => a.kind === "damage_photo");

  return (
    <div className="space-y-4 max-w-[1200px] mx-auto" data-testid="av-detail-page">
      {/* Back link */}
      <button
        onClick={() => navigate("/av/workbench")}
        className="inline-flex items-center gap-1 text-[12px] text-yamaha-700 hover:text-yamaha-800 font-semibold"
        data-testid="av-back"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Workbench
      </button>

      {/* Header card */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl p-4">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-lg bg-yamaha-50 flex items-center justify-center flex-shrink-0">
            <CarFront className="w-6 h-6 text-yamaha-700" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-mono text-[18px] font-bold text-yamaha-700">{av.av_number}</span>
              <span className={`inline-block text-[10.5px] font-bold uppercase tracking-wider border rounded-full px-2 py-0.5 ${AV_STATUS_TONES[av.status]}`}>
                {statusLabel(av.status)}
              </span>
              {av.on_hold && (
                <span className="text-[10.5px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5">
                  On Hold · {av.hold_days || 0}d
                </span>
              )}
              {av.loc_active && <LocActiveBadge caseId={av.active_loc_case_id} testIdPrefix="av-detail" />}
            </div>
            <div className="text-[15px] font-semibold text-[#1a1b22] mt-1">{av.customer_name}</div>
            <div className="text-[12px] text-[#444653]">
              {av.mobile_number} · {av.model_name} · Reg <span className="font-mono font-semibold">{av.registration_number}</span> · JC <span className="font-mono font-semibold">{av.job_card_no}</span>
            </div>
          </div>

          <div className="flex flex-col items-end gap-2">
            <AgeingBadge bucket={av.ageing_bucket} days={av.actual_working_days || 0} size="lg" label="Working Days" />
            <div className="text-[10.5px] text-[#747684]">
              Total {av.total_ageing_days || 0}d · Hold {av.hold_days || 0}d
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          <HeaderStat
            label="Received On"
            value={av.receiving_date ? fmtDate(av.receiving_date) : "—"}
            icon={Calendar}
            tone="emerald"
            big
          />
          <HeaderStat
            label="Likely Delivery"
            value={av.likely_delivery_date ? fmtDate(av.likely_delivery_date) : "—"}
            icon={Calendar}
            tone="violet"
            big
          />
          <HeaderStat label="Created" value={fmtDateTime(av.created_at)} sub={av.created_by} />
          <HeaderStat label="Last Updated" value={fmtDateTime(av.updated_at)} sub={av.updated_by} />
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          {!editing && canEdit && (
            <Button onClick={startEdit} data-testid="av-edit-btn" variant="outline">
              <Pencil className="w-4 h-4 mr-2" /> Edit
            </Button>
          )}
          {editing && (
            <>
              <Button onClick={saveEdit} disabled={saving} data-testid="av-save-btn" className="bg-yamaha-700 hover:bg-yamaha-800">
                <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Changes"}
              </Button>
              <Button onClick={() => setEditing(false)} variant="outline" data-testid="av-cancel-edit-btn">
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
            </>
          )}
          {canAct && av.status !== "delivered" && (
            <>
              {!av.on_hold ? (
                <Button onClick={() => setHoldOpen(true)} variant="outline" data-testid="av-hold-btn" className="border-rose-200 text-rose-700 hover:bg-rose-50">
                  <PauseCircle className="w-4 h-4 mr-2" /> Hold
                </Button>
              ) : (
                <Button onClick={() => setResumeOpen(true)} variant="outline" data-testid="av-resume-btn" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <PlayCircle className="w-4 h-4 mr-2" /> Resume
                </Button>
              )}
              {!av.on_hold && (() => {
                const billingPaid = (av.billing?.payment_status || "pending") === "paid";
                return (
                  <Button
                    onClick={() => setDeliverOpen(true)}
                    disabled={!billingPaid}
                    title={billingPaid ? "" : "Billing must be marked Paid before delivering."}
                    data-testid="av-deliver-btn"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Truck className="w-4 h-4 mr-2" />
                    {billingPaid ? "Mark Delivered" : "Mark Delivered (billing pending)"}
                  </Button>
                );
              })()}
              {/* Cancel AV — soft-cancel via status change */}
              {av.status !== "cancelled" && (
                <Button onClick={() => setCancelOpen(true)} variant="outline" data-testid="av-cancel-btn"
                  className="border-orange-200 text-orange-700 hover:bg-orange-50">
                  <Ban className="w-4 h-4 mr-2" /> Cancel AV
                </Button>
              )}
            </>
          )}
          {av.status !== "delivered" && av.status !== "cancelled" && (
            <>
              {!av.loc_active ? (
                canLocWrite && (
                  <Button onClick={() => setCnrOpen(true)} variant="outline" data-testid="av-cnr-btn"
                    className="border-amber-200 text-amber-700 hover:bg-amber-50">
                    <AlertTriangle className="w-4 h-4 mr-2" /> Customer Not Responding
                  </Button>
                )
              ) : (
                canLocView && (
                  <Button onClick={() => navigate(`/loc/${av.active_loc_case_id}`)} variant="outline" data-testid="av-view-loc-btn"
                    className="border-amber-200 text-amber-700 hover:bg-amber-50">
                    <EyeIcon className="w-4 h-4 mr-2" /> View LOC Case
                  </Button>
                )
              )}
            </>
          )}
          {/* Admin-only Delete AV — permanent */}
          {currentUser?.role === "admin" && (
            <Button onClick={() => setDeleteOpen(true)} variant="outline" data-testid="av-delete-btn"
              className="border-rose-300 text-rose-700 hover:bg-rose-50">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border border-[#e2e1eb] rounded-xl overflow-hidden">
        <div className="flex border-b border-[#e2e1eb] overflow-x-auto">
          {[
            { key: "overview", label: "Overview", enabled: true },
            { key: "estimate", label: "Estimate", enabled: true },
            { key: "documents", label: "Documents", enabled: true },
            { key: "parts", label: "Parts", enabled: true },
            { key: "repair", label: "Repair", enabled: true },
            {
              key: "billing",
              label: "Billing",
              // Spec: Billing activates only after repair Work Completed (or
              // if we're already past it).
              enabled:
                av.repair_section?.repair_status === "completed" ||
                ["billing_pending", "ready_delivery", "delivered"].includes(av.status),
            },
            { key: "advance", label: "Advance Money", enabled: true },
            { key: "crm", label: "CRM Notes", enabled: true },
            { key: "timeline", label: "Timeline", enabled: true },
          ].map((t) => (
            <button
              key={t.key}
              disabled={!t.enabled}
              onClick={() => t.enabled && setTab(t.key)}
              data-testid={`av-tab-${t.key}`}
              className={`px-4 py-2.5 text-[12.5px] font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === t.key && t.enabled
                  ? "border-yamaha-600 text-yamaha-700 bg-yamaha-50/50"
                  : t.enabled
                    ? "border-transparent text-[#444653] hover:bg-[#faf8ff]"
                    : "border-transparent text-[#a0a2b0] cursor-not-allowed"
              }`}
              title={t.enabled ? "" : (t.key === "billing" ? "Activates after Repair → Work Completed" : "Coming soon")}
            >
              {t.label}{!t.enabled && " · soon"}
            </button>
          ))}
        </div>

        <div className="p-4 space-y-4">
          {tab === "overview" && (
            <div data-testid="av-overview-tab" className="space-y-4">
              <Section title="Customer Details">
            <Grid>
              <Row label="Customer Name" value={editing ? <Input value={form.customer_name} onChange={(v) => set("customer_name", v)} /> : av.customer_name} />
              <Row label="Mobile" value={editing ? <Input value={form.mobile_number} onChange={(v) => set("mobile_number", v.replace(/\D/g, "").slice(0, 12))} /> : av.mobile_number} />
              <Row label="Alternate" value={editing ? <Input value={form.alternate_number || ""} onChange={(v) => set("alternate_number", v.replace(/\D/g, "").slice(0, 12))} /> : av.alternate_number || "—"} />
              <Row label="Town" value={editing ? <Input value={form.town || ""} onChange={(v) => set("town", v)} /> : av.town || "—"} />
              <Row wide label="Address" value={editing ? <Input value={form.address || ""} onChange={(v) => set("address", v)} /> : av.address || "—"} />
            </Grid>
          </Section>

          <Section title="Vehicle Details">
            <Grid>
              <Row label="Model" value={editing ? <Input value={form.model_name} onChange={(v) => set("model_name", v)} /> : av.model_name} />
              <Row label="Registration No" value={editing ? <Input value={form.registration_number} onChange={(v) => set("registration_number", v.toUpperCase())} mono /> : av.registration_number} mono />
              <Row label="VIN" value={editing ? <Input value={form.vin_no || ""} onChange={(v) => set("vin_no", v.toUpperCase())} mono /> : av.vin_no || "—"} mono />
              <Row label="Job Card" value={editing ? <Input value={form.job_card_no} onChange={(v) => set("job_card_no", v)} mono /> : av.job_card_no} mono />
              <Row label="KM Reading" value={editing ? <Input value={form.km_reading || ""} onChange={(v) => set("km_reading", v.replace(/\D/g, ""))} /> : (av.km_reading ?? "—")} />
            </Grid>
          </Section>

          <Section title="Claim & Status">
            <Grid>
              <Row label="Claim Type" value={
                editing ? (
                  <select value={form.claim_type} onChange={(e) => set("claim_type", e.target.value)} className={inputCls}>
                    {CLAIM_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                ) : (CLAIM_TYPES.find((c) => c.value === av.claim_type)?.label || av.claim_type)
              } />
              {((editing ? form.claim_type : av.claim_type) === "insured") && (
                <>
                  <Row label="Insurance Co." value={editing ? <Input value={form.insurance_company || ""} onChange={(v) => set("insurance_company", v)} /> : av.insurance_company || "—"} />
                  <Row label="Policy End" value={editing ? <Input type="date" value={form.policy_end_date || ""} onChange={(v) => set("policy_end_date", v)} /> : (av.policy_end_date ? fmtDate(av.policy_end_date) : "—")} />
                </>
              )}
              <Row label="Status" value={
                editing ? (
                  <select value={form.status} onChange={(e) => set("status", e.target.value)} className={inputCls} data-testid="av-status-select">
                    {AV_STATUSES.filter((s) => !["on_hold", "delivered"].includes(s.value)).map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                ) : statusLabel(av.status)
              } />
              <Row label="Received On" value={editing ? <Input type="date" value={form.receiving_date || ""} onChange={(v) => set("receiving_date", v)} /> : (av.receiving_date ? fmtDate(av.receiving_date) : "—")} />
              <Row label="Likely Delivery" value={editing ? <Input type="date" value={form.likely_delivery_date || ""} onChange={(v) => set("likely_delivery_date", v)} /> : (av.likely_delivery_date ? fmtDate(av.likely_delivery_date) : "—")} />
              <Row wide label="Complaint / Damage" value={editing ? <Textarea value={form.complaint || ""} onChange={(v) => set("complaint", v)} /> : (av.complaint || "—")} />
              <Row wide label="Internal Remarks" value={editing ? <Textarea value={form.internal_remarks || ""} onChange={(v) => set("internal_remarks", v)} /> : (av.internal_remarks || "—")} />
            </Grid>
          </Section>

          {/* Photos — upload + view + delete */}
          <Section title="Photos">
            <PhotoUploader
              av={av}
              currentUser={currentUser}
              uploading={uploading}
              setUploading={setUploading}
              onUpload={(u) => setAv(u)}
            />
            <PhotoGrid title="Vehicle Photos" photos={vehiclePhotos} avId={av.id}
              onDelete={async (attId) => {
                if (!window.confirm("Delete this photo?")) return;
                try {
                  await avDeleteAttachment(av.id, attId, { actor: currentUser.name, actor_role: currentUser.role });
                  const fresh = await avGet(av.id); setAv(fresh);
                  toast.success("Photo deleted");
                } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
              }}
            />
            <PhotoGrid title="Damage Photos" photos={damagePhotos} avId={av.id}
              onDelete={async (attId) => {
                if (!window.confirm("Delete this photo?")) return;
                try {
                  await avDeleteAttachment(av.id, attId, { actor: currentUser.name, actor_role: currentUser.role });
                  const fresh = await avGet(av.id); setAv(fresh);
                  toast.success("Photo deleted");
                } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
              }}
            />
          </Section>
            </div>
          )}

          {tab === "estimate" && <EstimateTab av={av} currentUser={currentUser} onChange={(u) => u ? setAv(u) : load()} />}
          {tab === "documents" && <DocumentsTab av={av} currentUser={currentUser} onChange={() => load()} />}
          {tab === "parts" && <PartsTab av={av} currentUser={currentUser} onChange={(u) => u ? setAv(u) : load()} />}
          {tab === "repair" && <RepairTab av={av} currentUser={currentUser} onChange={(u) => u ? setAv(u) : load()} />}
          {tab === "billing" && <BillingTab av={av} currentUser={currentUser} onChange={(u) => u ? setAv(u) : load()} />}
          {tab === "advance" && <AdvanceTab av={av} currentUser={currentUser} onChange={(u) => u ? setAv(u) : load()} />}
          {tab === "crm" && <CrmNotesTab av={av} currentUser={currentUser} onChange={(u) => u ? setAv(u) : load()} />}
          {tab === "timeline" && <TimelineTab avId={av.id} />}
        </div>
      </div>

      <HoldDialog open={holdOpen} onClose={() => setHoldOpen(false)} onSubmit={async (body) => {
        try { const u = await avHold(av.id, { ...body, actor: currentUser.name, actor_role: currentUser.role }); setAv(u); toast.success("AV placed on hold"); setHoldOpen(false); }
        catch (e) { toast.error(e?.response?.data?.detail || "Hold failed"); }
      }} />
      <SimpleConfirm open={resumeOpen} title={`Resume ${av.av_number}?`} description="Working-day ageing timer will restart." onClose={() => setResumeOpen(false)} onConfirm={async () => {
        try { const u = await avResume(av.id, { actor: currentUser.name, actor_role: currentUser.role }); setAv(u); toast.success("AV resumed"); setResumeOpen(false); }
        catch (e) { toast.error(e?.response?.data?.detail || "Resume failed"); }
      }} />
      <SimpleConfirm open={deliverOpen} title={`Mark ${av.av_number} as delivered?`} description="This locks the AV from further edits." confirmLabel="Mark Delivered" confirmClass="bg-emerald-600 hover:bg-emerald-700" onClose={() => setDeliverOpen(false)} onConfirm={async () => {
        try { const u = await avDeliver(av.id, { actor: currentUser.name, actor_role: currentUser.role }); setAv(u); toast.success("AV delivered"); setDeliverOpen(false); }
        catch (e) { toast.error(e?.response?.data?.detail || "Deliver failed"); }
      }} />
      <SimpleConfirm open={cancelOpen} title={`Cancel ${av.av_number}?`}
        description="This marks the AV as Cancelled. The record stays for audit; you can view but not edit it."
        confirmLabel="Cancel AV" confirmClass="bg-orange-600 hover:bg-orange-700"
        onClose={() => setCancelOpen(false)}
        onConfirm={async () => {
          try {
            const u = await avUpdate(av.id, { status: "cancelled", actor: currentUser.name, actor_role: currentUser.role });
            setAv(u);
            toast.success("AV cancelled");
            setCancelOpen(false);
          } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
        }} />
      <SimpleConfirm open={deleteOpen} title={`Permanently delete ${av.av_number}?`}
        description="This removes the AV, its photos, and all history. Cannot be undone."
        confirmLabel="Delete Permanently" confirmClass="bg-rose-600 hover:bg-rose-700"
        onClose={() => setDeleteOpen(false)}
        onConfirm={async () => {
          try {
            await avDelete(av.id);
            toast.success("AV deleted");
            setDeleteOpen(false);
            navigate("/av/workbench");
          } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
        }} />
      {cnrOpen && (
        <LocTriggerModal
          source={av}
          sourceType="av"
          actorName={currentUser?.name}
          actorRole={currentUser?.role}
          onClose={() => setCnrOpen(false)}
          onSuccess={() => load()}
        />
      )}
    </div>
  );
};

const inputCls =
  "w-full border border-[#e2e1eb] rounded-md px-2.5 py-1.5 text-[13px] focus:outline-none focus:border-yamaha-400";

const Input = ({ value, onChange, type = "text", mono }) => (
  <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} ${mono ? "font-mono uppercase" : ""}`} />
);
const Textarea = ({ value, onChange }) => (
  <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2} className={inputCls} />
);

const Section = ({ title, children }) => (
  <div className="border border-[#ece9f5] rounded-lg overflow-hidden">
    <div className="px-3 py-2 border-b border-[#ece9f5] bg-[#faf8ff] text-[12px] font-semibold text-[#1a1b22]">{title}</div>
    <div className="p-3 space-y-3">{children}</div>
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5">{children}</div>
);

const Row = ({ label, value, wide, mono }) => (
  <div className={`flex items-start gap-3 ${wide ? "md:col-span-2" : ""}`}>
    <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684] w-[35%] flex-shrink-0 pt-1.5">
      {label}
    </div>
    <div className={`text-[12.5px] text-[#1a1b22] flex-1 ${mono ? "font-mono" : ""}`}>{value}</div>
  </div>
);

const HeaderStat = ({ label, value, sub, tone, big, icon: Icon }) => {
  const tones = {
    violet: "bg-violet-50 border-violet-200 text-violet-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
  };
  return (
    <div className={`border rounded-lg p-3 ${tones[tone] || "bg-[#faf8ff] border-[#ece9f5]"}`}>
      <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#747684] flex items-center gap-1.5">
        {Icon && <Icon className="w-3 h-3" />}{label}
      </div>
      <div className={`mt-0.5 font-semibold ${big ? "text-[18px]" : "text-[13px]"} text-[#1a1b22]`}>{value}</div>
      {sub && <div className="text-[11px] text-[#747684] mt-0.5">{sub}</div>}
    </div>
  );
};

const PhotoGrid = ({ title, photos, avId, onDelete }) => {
  if (photos.length === 0) return null;
  return (
    <div className="mt-3">
      <div className="text-[11px] font-semibold text-[#444653] mb-2">{title} ({photos.length})</div>
      <div className="flex flex-wrap gap-2">
        {photos.map((p) => (
          <div key={p.id} className="relative group w-24 h-24 rounded border border-[#e2e1eb] overflow-hidden">
            <a href={avAttachmentUrl(avId, p.id)} target="_blank" rel="noopener noreferrer"
               data-testid={`av-photo-view-${p.id}`}
               className="block w-full h-full hover:opacity-80">
              <img src={avAttachmentUrl(avId, p.id)} alt={p.filename} className="object-cover w-full h-full" />
            </a>
            {onDelete && (
              <button
                onClick={(e) => { e.preventDefault(); onDelete(p.id); }}
                data-testid={`av-photo-delete-${p.id}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 flex items-center justify-center hover:bg-rose-600 transition-opacity"
                title="Delete photo"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Photo Uploader — allows adding Vehicle / Damage photos to an existing AV.
const PhotoUploader = ({ av, currentUser, uploading, setUploading, onUpload }) => {
  const [kind, setKind] = useState("vehicle_photo");
  const fileInputRef = React.useRef();

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      let updated = av;
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("kind", kind);
        fd.append("actor", currentUser.name);
        fd.append("actor_role", currentUser.role);
        const res = await avUploadAttachment(av.id, fd);
        updated = res.av || res;
      }
      onUpload(updated);
      toast.success(`Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };
  return (
    <div className="flex items-center gap-2 bg-[#faf8ff] border border-dashed border-[#e2e1eb] rounded-lg p-3">
      <select value={kind} onChange={(e) => setKind(e.target.value)}
        data-testid="av-photo-kind"
        className="border border-[#e2e1eb] rounded-md px-2 py-1.5 text-[12px] bg-white">
        <option value="vehicle_photo">Vehicle Photo</option>
        <option value="damage_photo">Damage Photo</option>
      </select>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="av-photo-upload"
        className="hidden"
        id={`av-photo-upload-${av.id}`}
      />
      <label htmlFor={`av-photo-upload-${av.id}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-yamaha-700 hover:bg-yamaha-800 text-white text-[12px] font-semibold cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}>
        <Upload className="w-3.5 h-3.5" />
        {uploading ? "Uploading…" : "Add Photo"}
      </label>
      <span className="text-[11px] text-[#747684]">JPG/PNG · up to a few MB · multiple allowed</span>
    </div>
  );
};

const HoldDialog = ({ open, onClose, onSubmit }) => {
  const [reason, setReason] = useState(HOLD_REASONS[0]);
  const [remarks, setRemarks] = useState("");
  const [expected, setExpected] = useState("");
  useEffect(() => { if (open) { setReason(HOLD_REASONS[0]); setRemarks(""); setExpected(""); } }, [open]);
  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Place AV on Hold</AlertDialogTitle>
          <AlertDialogDescription>Working-day ageing timer pauses while on hold.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-3 my-2">
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Reason</label>
            <select value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} data-testid="av-hold-reason">
              {HOLD_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Expected Resume Date</label>
            <input type="date" value={expected} onChange={(e) => setExpected(e.target.value)} className={inputCls} data-testid="av-hold-expected" />
          </div>
          <div>
            <label className="text-[11px] font-semibold uppercase tracking-wider text-[#747684] block mb-1">Remarks</label>
            <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2} className={inputCls} data-testid="av-hold-remarks" />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="av-hold-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onSubmit({ reason, remarks, expected_resume_date: expected || null })} className="bg-rose-600 hover:bg-rose-700" data-testid="av-hold-confirm">
            Place on Hold
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

const SimpleConfirm = ({ open, title, description, onClose, onConfirm, confirmLabel = "Confirm", confirmClass = "bg-yamaha-700 hover:bg-yamaha-800" }) => (
  <AlertDialog open={open} onOpenChange={(v) => !v && onClose()}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} className={confirmClass}>{confirmLabel}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export default AvDetail;
